const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const GTM_CONTAINER_ID = process.env.REACT_APP_GTM_CONTAINER_ID;
const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '4444848282428868';
const GOOGLE_ADS_CONVERSION_LABEL_FORM_SUBMIT = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL_FORM_SUBMIT;
const GOOGLE_ADS_CONVERSION_LABEL_WHATSAPP = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL_WHATSAPP;
const LINKEDIN_CONVERSION_ID_FORM_SUBMIT = process.env.REACT_APP_LINKEDIN_CONVERSION_ID_FORM_SUBMIT;
const LINKEDIN_CONVERSION_ID_WHATSAPP = process.env.REACT_APP_LINKEDIN_CONVERSION_ID_WHATSAPP;
const CONSENT_COOKIE_NAME = 'r5_cookie_consent';
const CONTENT_CHANNEL = process.env.REACT_APP_CHANNEL || null;
const CLIENT_ID_STORAGE_KEY = 'r5_analytics_client_id';
const SESSION_STORAGE_KEY = 'r5_analytics_session';
const FIRST_TOUCH_STORAGE_KEY = 'r5_analytics_first_touch';
const LAST_TOUCH_STORAGE_KEY = 'r5_analytics_last_touch';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const EXCLUDED_ANALYTICS_PATH_PREFIXES = ['/admin', '/login', '/portal-cliente', '/design-system'];
const OPERATIONAL_ANALYTICS_PATTERN = /\b(test|teste|smtp|codex|qa|homolog|interno)\b/i;
const COOKIE_MAX_CAPTURED_ITEMS = 40;
const COOKIE_NAME_MAX_LEN = 96;
const COOKIE_VALUE_MAX_LEN = 220;
const COOKIE_USEFUL_PATTERNS = [
  /^r5_cookie_consent$/i,
  /^_ga($|_)/i,
  /^_gid$/i,
  /^_gat/i,
  /^_gcl/i,
  /^_fbp$/i,
  /^_fbc$/i,
  /^googtrans$/i,
  /^_uet/i,
  /^li_gc$/i,
  /^ln_or$/i,
  /^_clck$/i,
  /^_clsk$/i,
];
const COOKIE_SENSITIVE_PATTERNS = [
  /token/i,
  /auth/i,
  /session/i,
  /csrf/i,
  /xsrf/i,
  /secret/i,
  /password/i,
  /jwt/i,
];

let gaInitialized = false;
let lastTrackedPath = '';
let clickTrackingBound = false;
let gtmInitialized = false;
let metaPixelInitialized = false;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const getCookie = (name) => {
  if (!isBrowser()) return null;
  const nameEQ = `${name}=`;
  const parts = document.cookie.split(';');

  for (let i = 0; i < parts.length; i += 1) {
    let cookie = parts[i];
    while (cookie.charAt(0) === ' ') cookie = cookie.substring(1);
    if (cookie.indexOf(nameEQ) === 0) return cookie.substring(nameEQ.length);
  }

  return null;
};

const readConsentPreferences = () => {
  const consentCookie = getCookie(CONSENT_COOKIE_NAME);
  if (!consentCookie) return null;

  try {
    return JSON.parse(consentCookie);
  } catch (firstError) {
    try {
      return JSON.parse(decodeURIComponent(consentCookie));
    } catch (secondError) {
      return null;
    }
  }
};

const normalizeApiUrl = (input) => {
  const fallback = '/api';
  const value = (input ?? '').toString().trim();
  if (!value) return fallback;

  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value.replace(/\/+$/, '');
  }

  const withSlash = value.startsWith('/') ? value : `/${value}`;
  return withSlash.replace(/\/+$/, '');
};

const API_URL = normalizeApiUrl(process.env.REACT_APP_API_URL);
const ANALYTICS_EVENTS_ENDPOINT = `${API_URL}/analytics/events`;

const safeJsonParse = (rawValue) => {
  if (!rawValue || typeof rawValue !== 'string') return null;
  try {
    return JSON.parse(rawValue);
  } catch (error) {
    return null;
  }
};

const readStorageJson = (storage, key) => {
  if (!isBrowser() || !storage || !key) return null;
  try {
    return safeJsonParse(storage.getItem(key));
  } catch (error) {
    return null;
  }
};

const writeStorageJson = (storage, key, value) => {
  if (!isBrowser() || !storage || !key) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore storage quota/privacy errors.
  }
};

const readStorageText = (storage, key) => {
  if (!isBrowser() || !storage || !key) return null;
  try {
    return storage.getItem(key);
  } catch (error) {
    return null;
  }
};

const writeStorageText = (storage, key, value) => {
  if (!isBrowser() || !storage || !key) return;
  try {
    storage.setItem(key, value);
  } catch (error) {
    // Ignore storage quota/privacy errors.
  }
};

const createId = (prefix = 'r5') => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return `${prefix}_${crypto.randomUUID()}`;
  }

  const randomPart = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}_${randomPart}`;
};

const normalizeText = (value) => {
  if (value === undefined || value === null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const normalizeGaParam = (value, maxLength = 100) => {
  const normalized = normalizeText(value);
  if (!normalized) return undefined;
  if (normalized.length <= maxLength) return normalized;
  return normalized.slice(0, maxLength);
};

const normalizeConversionId = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) return null;
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeConversionLabel = (value) => {
  const normalized = normalizeText(value);
  return normalized || null;
};

const trackLinkedInConversionById = (conversionId) => {
  if (!isBrowser()) return false;
  const normalizedId = normalizeConversionId(conversionId);
  if (!normalizedId || typeof window.lintrk !== 'function') return false;

  try {
    window.lintrk('track', { conversion_id: normalizedId });
    return true;
  } catch (error) {
    return false;
  }
};

const trackGoogleAdsConversionByLabel = (conversionLabel, options = {}) => {
  if (!isBrowser() || typeof window.gtag !== 'function') return false;
  if (!GOOGLE_ADS_ID || !hasMarketingConsent()) return false;

  const normalizedLabel = normalizeConversionLabel(conversionLabel);
  if (!normalizedLabel) return false;

  try {
    window.gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_ID}/${normalizedLabel}`,
      value: Number.isFinite(Number(options.value)) ? Number(options.value) : 1,
      currency: normalizeText(options.currency) || 'BRL',
      ...(options.extraParams && typeof options.extraParams === 'object' ? options.extraParams : {}),
    });
    return true;
  } catch (error) {
    return false;
  }
};

const ensureMetaPixelRuntime = () => {
  if (!isBrowser() || !META_PIXEL_ID || metaPixelInitialized) return;

  if (typeof window.fbq !== 'function') {
    const fbq = function () {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, arguments);
      } else {
        fbq.queue.push(arguments);
      }
    };

    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];

    window.fbq = fbq;
    window._fbq = fbq;
  }

  const existingScript = document.getElementById('r5-meta-pixel-script');
  if (!existingScript) {
    const script = document.createElement('script');
    script.id = 'r5-meta-pixel-script';
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }

  try {
    window.fbq('init', META_PIXEL_ID);
  } catch (error) {
    return;
  }

  metaPixelInitialized = true;
};

const setMetaPixelConsent = (preferences) => {
  if (!isBrowser() || !META_PIXEL_ID || typeof window.fbq !== 'function') return;

  try {
    window.fbq('consent', preferences?.marketing ? 'grant' : 'revoke');
  } catch (error) {
    // Ignore consent/runtime mismatches from the Meta script bootstrap.
  }
};

const trackMetaPixelEvent = (eventName, params = {}) => {
  if (!isBrowser() || !META_PIXEL_ID || !hasMarketingConsent() || typeof window.fbq !== 'function') {
    return false;
  }

  const normalizedEventName = normalizeText(eventName);
  if (!normalizedEventName) return false;

  try {
    window.fbq('trackSingle', META_PIXEL_ID, normalizedEventName, params);
    return true;
  } catch (error) {
    return false;
  }
};

const isPathExcludedFromAnalytics = (pathname = '') => {
  const normalizedPath = normalizeText(pathname) || '/';
  return EXCLUDED_ANALYTICS_PATH_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix));
};

const isOperationalAnalyticsMetadata = (metadata = {}) => {
  if (!metadata || typeof metadata !== 'object') return false;
  if (metadata.operational === true || metadata.reportingScope === 'operational') return true;

  const haystack = [
    normalizeText(metadata.email),
    normalizeText(metadata.name),
    normalizeText(metadata.message),
    normalizeText(metadata.reason),
    normalizeText(metadata.fieldName),
  ]
    .filter(Boolean)
    .join(' ');

  return OPERATIONAL_ANALYTICS_PATTERN.test(haystack);
};

const getOrCreateClientId = () => {
  if (!isBrowser()) return null;
  const existing = normalizeText(readStorageText(window.localStorage, CLIENT_ID_STORAGE_KEY));
  if (existing) return existing;
  const created = createId('r5c');
  writeStorageText(window.localStorage, CLIENT_ID_STORAGE_KEY, created);
  return created;
};

const touchSession = () => {
  if (!isBrowser()) {
    return {
      sessionId: null,
      sessionStartedAt: null,
      sessionEventIndex: null,
    };
  }

  const now = Date.now();
  const current = readStorageJson(window.sessionStorage, SESSION_STORAGE_KEY);
  const parsedLastSeenAt =
    current && current.lastSeenAt !== undefined ? Number(current.lastSeenAt) : null;
  const isExpired =
    !current ||
    !normalizeText(current.id) ||
    !parsedLastSeenAt ||
    Number.isNaN(parsedLastSeenAt) ||
    now - parsedLastSeenAt > SESSION_TIMEOUT_MS;

  const startedAt = isExpired
    ? new Date(now).toISOString()
    : normalizeText(current.startedAt) || new Date(now).toISOString();
  const eventIndexBase = isExpired ? 0 : Number(current.eventIndex || 0);
  const sessionId = isExpired ? createId('r5s') : normalizeText(current.id);
  const nextSession = {
    id: sessionId || createId('r5s'),
    startedAt,
    lastSeenAt: now,
    eventIndex: Number.isNaN(eventIndexBase) ? 1 : eventIndexBase + 1,
  };

  writeStorageJson(window.sessionStorage, SESSION_STORAGE_KEY, nextSession);

  return {
    sessionId: nextSession.id,
    sessionStartedAt: nextSession.startedAt,
    sessionEventIndex: nextSession.eventIndex,
  };
};

const ensureGtagRuntime = () => {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
};

const setConsentMode = (preferences) => {
  if (!isBrowser() || !window.gtag) return;

  const analyticsGranted = Boolean(preferences?.analytics);
  const marketingGranted = Boolean(preferences?.marketing);

  window.gtag('consent', 'update', {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    ad_storage: marketingGranted ? 'granted' : 'denied',
    ad_user_data: marketingGranted ? 'granted' : 'denied',
    ad_personalization: marketingGranted ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
};

const parseUtmParams = (searchValue = '') => {
  if (!isBrowser()) return {};
  const params = new URLSearchParams(searchValue || window.location.search);
  return {
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
    utmTerm: params.get('utm_term'),
    utmContent: params.get('utm_content'),
  };
};

const extractHost = (urlValue) => {
  const normalizedValue = normalizeText(urlValue);
  if (!normalizedValue) return null;
  try {
    const urlObj = new URL(normalizedValue, window.location.href);
    return normalizeText(urlObj.hostname?.toLowerCase());
  } catch (error) {
    return null;
  }
};

const parseReferrer = (referrerValue) => {
  const normalizedReferrer = normalizeText(referrerValue);
  if (!normalizedReferrer) {
    return {
      referrerUrl: null,
      referrerHost: null,
      isExternal: false,
    };
  }

  const referrerHost = extractHost(normalizedReferrer);
  const currentHost = normalizeText(window.location.hostname?.toLowerCase());
  const isExternal = Boolean(referrerHost && currentHost && referrerHost !== currentHost);

  return {
    referrerUrl: normalizedReferrer,
    referrerHost: isExternal ? referrerHost : null,
    isExternal,
  };
};

const resolveTrafficSource = ({ utm, referrerHost }) => {
  const utmSource = normalizeText(utm?.utmSource);
  const utmMedium = normalizeText(utm?.utmMedium);
  const utmCampaign = normalizeText(utm?.utmCampaign);

  if (utmSource) {
    return {
      source: utmSource,
      medium: utmMedium || 'utm',
      campaign: utmCampaign,
      isDirect: false,
    };
  }

  if (referrerHost) {
    return {
      source: referrerHost,
      medium: 'referral',
      campaign: null,
      isDirect: false,
    };
  }

  return {
    source: '(direct)',
    medium: '(none)',
    campaign: null,
    isDirect: true,
  };
};

const getOrCreateFirstTouch = (candidate) => {
  if (!isBrowser()) return null;
  const current = readStorageJson(window.localStorage, FIRST_TOUCH_STORAGE_KEY);
  if (current && normalizeText(current.source) && normalizeText(current.medium)) {
    return current;
  }

  const snapshot = {
    source: normalizeText(candidate.source) || '(direct)',
    medium: normalizeText(candidate.medium) || '(none)',
    campaign: normalizeText(candidate.campaign),
    referrerHost: normalizeText(candidate.referrerHost),
    landingPath: normalizeText(candidate.pagePath) || window.location.pathname,
    capturedAt: new Date().toISOString(),
  };
  writeStorageJson(window.localStorage, FIRST_TOUCH_STORAGE_KEY, snapshot);
  return snapshot;
};

const storeLastTouch = (snapshot) => {
  if (!isBrowser()) return snapshot;
  writeStorageJson(window.localStorage, LAST_TOUCH_STORAGE_KEY, snapshot);
  return snapshot;
};

const buildAttribution = ({ pagePath, referrer, search }) => {
  const utm = parseUtmParams(search);
  const parsedReferrer = parseReferrer(referrer ?? document.referrer);
  const resolved = resolveTrafficSource({ utm, referrerHost: parsedReferrer.referrerHost });

  const firstTouch = getOrCreateFirstTouch({
    ...resolved,
    referrerHost: parsedReferrer.referrerHost,
    pagePath,
  });
  const lastTouch = storeLastTouch({
    source: resolved.source,
    medium: resolved.medium,
    campaign: resolved.campaign,
    referrerHost: parsedReferrer.referrerHost,
    landingPath: pagePath,
    capturedAt: new Date().toISOString(),
  });

  return {
    utm,
    referrerUrl: parsedReferrer.referrerUrl,
    referrerHost: parsedReferrer.referrerHost,
    source: resolved.source,
    medium: resolved.medium,
    campaign: resolved.campaign,
    isDirect: resolved.isDirect,
    firstTouch,
    lastTouch,
  };
};

const getTelemetrySnapshot = () => {
  if (!isBrowser()) return {};
  return {
    language: navigator.language || null,
    screen: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
  };
};

const safeDecodeCookieValue = (value) => {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

const captureCookieSnapshot = () => {
  if (!isBrowser()) return null;
  const rawCookie = String(document.cookie || '');
  const trimmed = rawCookie.trim();
  if (!trimmed) {
    return {
      totalCount: 0,
      usefulCount: 0,
      names: [],
      useful: {},
    };
  }

  const entries = trimmed
    .split(';')
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .slice(0, COOKIE_MAX_CAPTURED_ITEMS)
    .map((chunk) => {
      const separatorIndex = chunk.indexOf('=');
      const rawName = separatorIndex >= 0 ? chunk.slice(0, separatorIndex) : chunk;
      const rawValue = separatorIndex >= 0 ? chunk.slice(separatorIndex + 1) : '';
      const name = normalizeText(rawName)?.slice(0, COOKIE_NAME_MAX_LEN) || null;
      const decodedValue = safeDecodeCookieValue(rawValue);
      const normalizedValue = normalizeText(decodedValue);
      const value = normalizedValue ? normalizedValue.slice(0, COOKIE_VALUE_MAX_LEN) : '';
      return { name, value };
    })
    .filter((entry) => Boolean(entry.name));

  const useful = {};
  entries.forEach((entry) => {
    const isUseful = COOKIE_USEFUL_PATTERNS.some((pattern) => pattern.test(entry.name));
    if (!isUseful) return;
    const isSensitive = COOKIE_SENSITIVE_PATTERNS.some((pattern) => pattern.test(entry.name));
    useful[entry.name] = isSensitive ? '[redacted]' : entry.value;
  });

  const snapshot = {
    totalCount: entries.length,
    usefulCount: Object.keys(useful).length,
    names: entries.map((entry) => entry.name),
    useful,
  };

  const consentCookie = useful[CONSENT_COOKIE_NAME];
  if (consentCookie) {
    const parsedConsent = safeJsonParse(consentCookie);
    if (parsedConsent && typeof parsedConsent === 'object') {
      snapshot.consent = {
        necessary: Boolean(parsedConsent.necessary),
        analytics: Boolean(parsedConsent.analytics),
        marketing: Boolean(parsedConsent.marketing),
        configuredAt: normalizeText(parsedConsent.timestamp) || null,
      };
    }
  }

  return snapshot;
};

const sendInternalAnalyticsEvent = (payload) => {
  if (!isBrowser()) return;

  fetch(ANALYTICS_EVENTS_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    // Intentionally silent: analytics must never block UX.
  });
};

const isAnalyticsAllowed = () => {
  if (!GA_MEASUREMENT_ID || !isBrowser() || !gaInitialized || !window.gtag) return false;
  const currentConsent = readConsentPreferences();
  return Boolean(currentConsent?.analytics);
};

const hasMarketingConsent = () => {
  if (!isBrowser()) return false;
  const currentConsent = readConsentPreferences();
  return Boolean(currentConsent?.marketing);
};

const isMarketingAllowed = () => hasMarketingConsent() && typeof window.lintrk === 'function';
const isMetaPixelAllowed = () => hasMarketingConsent() && typeof window.fbq === 'function';

const ensureGtmRuntime = () => {
  if (!isBrowser() || !GTM_CONTAINER_ID || gtmInitialized) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': Date.now(),
    event: 'gtm.js',
  });

  const existingScript = document.getElementById('r5-gtm-script');
  if (!existingScript) {
    const script = document.createElement('script');
    script.id = 'r5-gtm-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
    document.head.appendChild(script);
  }

  gtmInitialized = true;
};

const pushGtmEvent = (eventName, payload = {}) => {
  if (!isBrowser() || !GTM_CONTAINER_ID) return;
  const normalizedEventName = normalizeText(eventName);
  if (!normalizedEventName) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'r5_event',
    r5_event_name: normalizedEventName,
    ...payload,
  });
};

const trackContactLinkClicks = () => {
  if (!isBrowser() || clickTrackingBound) return;
  clickTrackingBound = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      const anchor = target && typeof target.closest === 'function'
        ? target.closest('a[href]')
        : null;
      if (!anchor) return;

      const hrefRaw = normalizeText(anchor.getAttribute('href'));
      if (!hrefRaw) return;

      const normalizedHref = hrefRaw.toLowerCase();
      const metadata = {
        href: hrefRaw,
        linkText: normalizeText(anchor.textContent)?.slice(0, 140) || null,
      };

      if (normalizedHref.startsWith('mailto:')) {
        if (isAnalyticsAllowed()) {
          trackEvent('contact_click_email', { metadata });
        }
        return;
      }

      if (
        normalizedHref.includes('wa.me/') ||
        normalizedHref.includes('api.whatsapp.com/')
      ) {
        if (isAnalyticsAllowed()) {
          trackEvent('contact_click_whatsapp', { metadata });
        }
        if (isMetaPixelAllowed()) {
          trackMetaPixelEvent('Contact', {
            content_name: 'whatsapp_click',
            content_category: 'lead',
            channel: CONTENT_CHANNEL || 'desktop',
          });
        }
        if (isMarketingAllowed()) {
          trackLinkedInConversionById(LINKEDIN_CONVERSION_ID_WHATSAPP);
        }
        trackGoogleAdsConversionByLabel(GOOGLE_ADS_CONVERSION_LABEL_WHATSAPP);
      }
    },
    true
  );
};

export const initializeAnalytics = () => {
  if (!isBrowser() || gaInitialized) return;

  if (GA_MEASUREMENT_ID || GOOGLE_ADS_ID) {
    ensureGtagRuntime();

    window.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
    });

    const existingScript = document.getElementById('r5-ga4-script');
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'r5-ga4-script';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID || GOOGLE_ADS_ID}`;
      document.head.appendChild(script);
    }

    window.gtag('js', new Date());
    if (GA_MEASUREMENT_ID) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        send_page_view: false,
        anonymize_ip: true,
      });
    }
    if (GOOGLE_ADS_ID) {
      window.gtag('config', GOOGLE_ADS_ID);
    }

    setConsentMode(readConsentPreferences());
  }

  if (hasMarketingConsent()) {
    ensureMetaPixelRuntime();
    setMetaPixelConsent(readConsentPreferences());
  }

  ensureGtmRuntime();

  gaInitialized = true;
  trackContactLinkClicks();
};

export const updateAnalyticsConsent = (preferences) => {
  if (!isBrowser()) return;

  if (GA_MEASUREMENT_ID || GOOGLE_ADS_ID) {
    ensureGtagRuntime();
    setConsentMode(preferences);
  }

  if (preferences?.marketing) {
    ensureMetaPixelRuntime();
  }
  setMetaPixelConsent(preferences);

  if (preferences?.marketing) {
    trackMetaPixelEvent('PageView');
  }
};

export const trackEvent = (eventName, options = {}) => {
  const normalizedEventName = normalizeText(eventName);
  if (!normalizedEventName || !isAnalyticsAllowed()) return;

  const resolvedPath = options.pathname || window.location.pathname;
  if (!options.allowRestrictedPath && isPathExcludedFromAnalytics(resolvedPath)) return;
  const resolvedSearch = options.search || window.location.search;
  const pagePath = `${resolvedPath}${resolvedSearch}`;
  const pageUrl = options.pageUrl || window.location.href;
  const pageTitle = options.pageTitle || document.title;
  const attribution = buildAttribution({
    pagePath,
    referrer: options.referrer,
    search: resolvedSearch,
  });
  const session = touchSession();
  const clientId = getOrCreateClientId();
  const metadata = options.metadata && typeof options.metadata === 'object' ? options.metadata : {};
  if (!options.allowOperational && isOperationalAnalyticsMetadata(metadata)) return;
  const funnelName = normalizeGaParam(metadata.funnel);
  const funnelStep = normalizeGaParam(metadata.step);
  const funnelFormId = normalizeGaParam(metadata.formId);
  const linkHref = normalizeGaParam(metadata.href, 240);
  const linkText = normalizeGaParam(metadata.linkText, 140);
  const gaEventParams = {
    r5_channel: normalizeGaParam(CONTENT_CHANNEL || 'desktop'),
    r5_source: normalizeGaParam(attribution.source),
    r5_medium: normalizeGaParam(attribution.medium),
    r5_campaign: normalizeGaParam(attribution.campaign || '(not set)'),
    r5_referrer_host: normalizeGaParam(attribution.referrerHost || '(none)'),
    r5_is_direct: attribution.isDirect ? '1' : '0',
    r5_first_source: normalizeGaParam(attribution.firstTouch?.source || '(direct)'),
    r5_first_medium: normalizeGaParam(attribution.firstTouch?.medium || '(none)'),
    r5_funnel: funnelName,
    r5_funnel_step: funnelStep,
    r5_form_id: funnelFormId,
    r5_link_href: linkHref,
    r5_link_text: linkText,
    r5_session_id: normalizeGaParam(session.sessionId, 64),
    r5_session_event_index: Number.isFinite(session.sessionEventIndex) ? String(session.sessionEventIndex) : undefined,
  };

  window.gtag('event', normalizedEventName, {
    page_title: pageTitle,
    page_path: pagePath,
    page_location: pageUrl,
    ...gaEventParams,
    ...(options.gaParams && typeof options.gaParams === 'object' ? options.gaParams : {}),
  });

  pushGtmEvent(normalizedEventName, {
    page_title: pageTitle,
    page_path: pagePath,
    page_location: pageUrl,
    ...gaEventParams,
  });

  sendInternalAnalyticsEvent({
    channel: CONTENT_CHANNEL,
    eventName: normalizedEventName,
    pageUrl,
    pagePath,
    pageTitle,
    referrer: attribution.referrerUrl,
    clientId,
    sessionId: session.sessionId,
    ...attribution.utm,
    metadata: {
      ...metadata,
      attribution: {
        source: attribution.source,
        medium: attribution.medium,
        campaign: attribution.campaign,
        referrerHost: attribution.referrerHost,
        isDirect: attribution.isDirect,
        firstTouch: attribution.firstTouch,
        lastTouch: attribution.lastTouch,
      },
      session: {
        startedAt: session.sessionStartedAt,
        eventIndex: session.sessionEventIndex,
      },
      telemetry: getTelemetrySnapshot(),
      cookieSnapshot: captureCookieSnapshot(),
    },
  });
};

export const trackPageView = (pathname = '', search = '') => {
  // Do not classify the ethics channel (including legacy links) as a lead page.
  const ethicsParams = new URLSearchParams(search || (isBrowser() ? window.location.search : ''));
  const ethicsPath = pathname || (isBrowser() ? window.location.pathname : '');
  if (ethicsPath.endsWith('/canal-de-etica') || ethicsPath.endsWith('/canal-de-etica/') ||
      ['etica', 'ethics'].includes(ethicsParams.get('canal')) ||
      (isBrowser() && window.location.hash === '#canal-de-etica')) return;
  const resolvedPath = pathname || (isBrowser() ? window.location.pathname : '');
  const resolvedSearch = search || (isBrowser() ? window.location.search : '');
  const pagePath = `${resolvedPath}${resolvedSearch}`;

  if (pagePath === lastTrackedPath) return;
  lastTrackedPath = pagePath;

  trackEvent('page_view', {
    pathname: resolvedPath,
    search: resolvedSearch,
  });

  if (isMetaPixelAllowed()) {
    trackMetaPixelEvent('PageView');
  }

  if (resolvedPath.startsWith('/fale-conosco')) {
    trackEvent('lead_form_view', {
      pathname: resolvedPath,
      search: resolvedSearch,
      metadata: {
        funnel: 'lead',
        step: 'form_view',
        formId: 'contact',
      },
    });
  }
};

export const trackLinkedInLeadFormSubmitConversion = () => {
  const linkedInTracked = isMarketingAllowed()
    ? trackLinkedInConversionById(LINKEDIN_CONVERSION_ID_FORM_SUBMIT)
    : false;
  const googleAdsTracked = trackGoogleAdsConversionByLabel(GOOGLE_ADS_CONVERSION_LABEL_FORM_SUBMIT);
  return linkedInTracked || googleAdsTracked;
};

export const trackMetaLeadFormSubmitConversion = () => trackMetaPixelEvent('Lead', {
  content_name: 'contact_form_submit',
  content_category: 'lead',
  channel: CONTENT_CHANNEL || 'desktop',
  currency: 'BRL',
  value: 1,
});

export const trackLinkedInWhatsAppConversion = () => {
  const linkedInTracked = isMarketingAllowed()
    ? trackLinkedInConversionById(LINKEDIN_CONVERSION_ID_WHATSAPP)
    : false;
  const googleAdsTracked = trackGoogleAdsConversionByLabel(GOOGLE_ADS_CONVERSION_LABEL_WHATSAPP);
  return linkedInTracked || googleAdsTracked;
};
